/**
 * SimilarInVendorWidget — Iter 69.
 *
 * Shows up to 3 OTHER cars from the same vendor's fleet that are likely
 * cheaper for this trip. Same-type matches surface first. Self-hides when
 * the API returns zero suggestions OR when ALL suggestions are MORE
 * expensive (in which case the BookEarlier / StayLonger nudges still
 * apply, but this widget would just clutter the UI).
 *
 * Click a row → React-Router navigation to that car's detail page,
 * preserving the current `start_date` + `end_date` query params so the
 * quote on the next page lands pre-filled.
 */
import { useEffect, useState } from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { logWidgetEvent } from "@/lib/widgetTracking";
import { Repeat, BadgePercent } from "lucide-react";
import Money from "@/components/Money";

export default function SimilarInVendorWidget({ carId, startIso, endIso }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!carId || !startIso || !endIso) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .post(`/cars/${carId}/similar-in-vendor`, { start_date: startIso, end_date: endIso })
        .then((r) => {
          if (!cancelled) setData(r.data);
        })
        .catch(() => {
          if (!cancelled) setData(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [carId, startIso, endIso]);

  if (!data || !data.options?.length) return null;
  // Self-hide if every option is MORE expensive — pure upsell with no
  // saving angle isn't a great nudge here (renters resent it).
  const savingsRows = data.options.filter((o) => (o.savings || 0) > 0.5);
  if (savingsRows.length === 0) return null;

  const best = savingsRows[0];
  logWidgetEvent("similar_in_vendor", "impression", carId);

  const goToCar = (otherId) => {
    logWidgetEvent("similar_in_vendor", "click", carId, { target_car_id: otherId });
    navigate({
      pathname: `/cars/${otherId}`,
      search: `?${createSearchParams({ start_date: startIso, end_date: endIso }).toString()}`,
    });
  };

  return (
    <div
      className="rounded-2xl border border-teal-200 bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 p-4 shadow-sm"
      data-testid="similar-in-vendor-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center ring-1 ring-teal-200">
          <Repeat className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold text-neutral-900">
            Same fleet, save <Money amount={best.savings} className="text-teal-700" />
          </div>
          <div className="text-[11px] text-neutral-500 truncate">
            {savingsRows.length} other car{savingsRows.length === 1 ? "" : "s"} from this vendor for the same dates
          </div>
        </div>
      </div>
      <ul className="space-y-1.5" data-testid="similar-in-vendor-options">
        {savingsRows.map((opt) => (
          <li key={opt.id}>
            <button
              type="button"
              onClick={() => goToCar(opt.id)}
              className="w-full flex items-center justify-between gap-3 rounded-xl bg-white/80 hover:bg-white border border-teal-100 hover:border-teal-300 hover:shadow-md transition-all px-3 py-2.5 text-left"
              data-testid={`similar-in-vendor-option-${opt.id}`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {opt.image ? (
                  <img
                    src={opt.image}
                    alt={opt.name}
                    className="h-9 w-12 object-cover rounded-md ring-1 ring-neutral-100 shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-9 w-12 rounded-md bg-neutral-100 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-neutral-800 truncate">{opt.name}</div>
                  <div className="text-[10px] text-neutral-500 truncate">
                    {[opt.car_type, opt.transmission, opt.fuel_type].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-teal-700 font-display inline-flex items-center gap-1">
                  <BadgePercent className="h-3 w-3" />−<Money amount={opt.savings} />
                </div>
                <div className="text-[10px] text-neutral-500">
                  Total <Money amount={opt.total} />
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
